import { createContext, useContext, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { findIana } from 'windows-iana';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import momentTz from 'moment-timezone';

import { UserDetailsContext } from 'context/userDetailsContext';
import { OutLookContext } from 'context/OutlookContext';

import { listContacts } from 'services/Contact/contactServices';
import { totalCountEvent } from 'services/generalServices';
import { getCalendarAttendees, listUsers } from 'services/users/userServices';
import { getOfficeLocations } from 'services/Settings';

import { getCalendarSettings, listEventTypes, listEvents } from 'services/Calender/calendarServices';
import { formatEventsOutLook, getUserWeekCalendar } from 'services/outLookServices';

import { generateFullName } from 'utils/generateFullNameUtils';
import { convertCaseToFilterFormat, formatMultiSelectContactList, getUserId, sortByKey } from 'utils/utils';

import { convertToTimezoneFormat, getTimezoneOffset } from 'utils/utility_functions/timezone';
import { useNavbarContext } from 'context/NavbarContext';

const CalendarContext = createContext({});

export const useCalendarContext = () => useContext(CalendarContext);

const getDateRangeInUserTimezone = (startDateStr, endDateStr, viewType, userTimezone) => {
  if (viewType === 'timeGridDay') {
    const selectedDate = momentTz.tz(startDateStr, userTimezone);
    return {
      date_filter_param: selectedDate.clone().startOf('day').format(),
      range_end_param: selectedDate.clone().endOf('day').format(),
    };
  }

  const start = momentTz.tz(startDateStr, userTimezone).startOf('day');
  const end = momentTz.tz(endDateStr, userTimezone).endOf('day');

  return {
    date_filter_param: start.format(),
    range_end_param: end.format(),
  };
};

export const CalendarContextProvider = (props) => {
  const { caseDetails, isFromCaseSpecificModule, isFromLeadSpecificModule, isClientCalendar, isGlobal, isClosed } = props;
  const location = useLocation();
  const history = useHistory();
  // Email Redirection logic
  const queryParams = new URLSearchParams(location?.search);
  const event_id = queryParams.get('id');
  const eventParam = queryParams.get('event');
  const skParam = queryParams.get('sk');

  const userContext = useContext(UserDetailsContext);
  const { readClientNotifications, notificationCounts } = useNavbarContext();
  const { user, displayError, authProvider, setUser } = useContext(OutLookContext);

  //lists
  const [locationsList, setLocationsList] = useState([]);
  const [officeLocationsList, setOfficeLocationsList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [userList, setUserList] = useState();
  const [firmUserList, setFirmUserList] = useState([]);

  // event list
  const [eventList, setEventList] = useState([]);
  const [outlookEventList, setOutlookEventList] = useState([]);

  //filters
  const [calendarSettings, setCalendarSettings] = useState();

  // Helper to parse initial attendeesFilter (handles both structured and legacy formats)
  const getInitialAttendeesFilter = () => {
    if (!isGlobal) return [];

    const savedUsers = userContext?.firmDetails?.user_preferences?.filters?.calender?.users;

    const isStructuredFormat =
      Array.isArray(savedUsers) && savedUsers.length > 0 && typeof savedUsers[0] === 'object' && savedUsers[0]?.firm_user_id;

    if (isStructuredFormat) {
      return [];
    }

    return Array.isArray(savedUsers) && savedUsers.length > 0 ? savedUsers : [getUserId()];
  };

  const [calendarFilters, setCalendarFilters] = useState({
    locationFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_location || '' : '',
    eventTypeFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.event_type || '' : '',
    practiceAreaFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_practice_area || '' : '',
    categoryFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_category || '' : '',
    attendeesFilter: getInitialAttendeesFilter(),
    caseFilter:
      isGlobal && !isClientCalendar
        ? userContext?.firmDetails?.user_preferences?.filters?.calender?.case || ''
        : isClientCalendar
        ? convertCaseToFilterFormat(userContext?.clientSelectedCase)
        : '',

    conferenceRoomFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.conference_room || '' : '',
    practiceAreaFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_practice_area || '' : '',
  });

  const [showFirmEvents, setShowFirmEvents] = useState(false);

  const [highlightedGoogleIconIds, setHighlightedGoogleIconIds] = useState(new Set());

  const lastManualAttendeesFilterRef = useRef(null);
  const lastManualUpdateTimeRef = useRef(null);

  //modals
  const [showCreateEventModal, setShowCreateEventModal] = useState(Boolean(location?.state?.createEvent || queryParams.get('createEvent')));
  const [showEventPreviewModal, setShowEventPreviewModal] = useState(Boolean(event_id || eventParam || skParam));
  const [editEventModal, setEditEventModal] = useState(false);
  const [editAllRecurringEvent, setEditAllRecurringEvent] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState();
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [tempDeletedAttachments, setTempDeletedAttachments] = useState([]);
  const [showUserFilter, setShowUserFilter] = useState(false);
  const [showReminder, setShowReminder] = useState(true);

  //event details
  const [eventDetails, setEventDetails] = useState();

  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [currentDates, setCurrentDates] = useState({
    currentStartValue: '',
    currentEndValue: '',
    currentStartDate: '',
    currentEndDate: '',
  });
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isEventTypeLoading, setIsEventTypeLoading] = useState(false);
  const [calendarDayClickedDetails, setCalendateDayClickedDetails] = useState({});
  const [calendarAttendeesLoaded, setCalendarAttendeesLoaded] = useState(false);

  const loadLocations = () => {
    setIsLocationLoading(true);
    totalCountEvent('contact', '', '', '', '', true)
      .then((response) => {
        let obj = {
          contactType: '',
          contactStatus: '',
          keyWord: '',
          is_court_location: true,
          is_event_location: true,
          pageLimit: response.data,
          excludeFromLoading: true,
        };

        listContacts(obj)
          .then((response) => {
            if (response && response.data && response.data.contacts && response.data.contacts.length > 0) {
              let options = response.data.contacts.map((index) => {
                return {
                  label: index.title ? index.title : index.office_name,
                  value: index.contact_id || index?.office_location_id,
                };
              });
              setLocationsList(options);
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => setIsLocationLoading(false));
      })
      .catch((err) => {
        setIsLocationLoading(false);
        console.log(err);
      });
  };

  const loadOfficeLocations = () => {
    getOfficeLocations()
      .then((response) => {
        if (response && response.data) {
          setOfficeLocationsList(response.data);
        }
      })
      .catch((err) => {
        console.log('Error loading office locations:', err);
      });
  };

  const loadTypes = (practice_area = '') => {
    setIsEventTypeLoading(true);
    totalCountEvent('event-type', '', '', '', '', true)
      .then((response) => {
        let obj = {
          pageLimit: response.data,
          pagination_token: '',
          pagination_direction: '',
          excludeFromLoading: true,
          practiceAreaId: practice_area,
        };
        listEventTypes(obj)
          .then((res) => {
            let data = res?.data['event-type'];
            let tempArray = [];

            data.forEach((item) => {
              tempArray.push({
                event_type: item.event_type,
                event_type_icon: item.event_type_icon,
                event_type_id: item.event_type_id,
              });
            });

            setTypeList(tempArray);
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => setIsEventTypeLoading(false));
      })
      .catch((err) => {
        setIsEventTypeLoading(false);
        console.log(err);
      });
  };

  const fetchOutlookEvents = () => {
    if (calendarSettings?.sync_with_outlook && user) {
      getOutlookEvents();
    } else {
      setOutlookEventList([]);
    }
  };

  function getEventList() {
    let { currentStartDate, currentEndDate } = currentDates;

    const noDates = !currentStartDate && !currentEndDate;
    const noCase = isClientCalendar && !(caseDetails?.case_id || calendarFilters.caseFilter?.id);

    // Avoid premature fetch on case/lead-specific calendars before firm users are loaded
    const awaitingFirmUsers =
      (isFromCaseSpecificModule || isFromLeadSpecificModule) &&
      (!firmUserList || firmUserList.length === 0) &&
      Array.isArray(calendarFilters?.attendeesFilter) &&
      calendarFilters.attendeesFilter.length === 0;

    const awaitingCalendarAttendees = !calendarAttendeesLoaded && !isClientCalendar;

    if (noDates || noCase || awaitingFirmUsers || awaitingCalendarAttendees) return;

    const startDate = moment(currentStartDate).format('YYYY-MM-DD');
    const endDate = moment(currentEndDate).format('YYYY-MM-DD');

    const { date_filter_param, range_end_param } = getDateRangeInUserTimezone(
      startDate,
      endDate,
      currentView,
      userContext?.userDetails?.timezone
    );

    const attendeesFilterFlat = (calendarFilters?.attendeesFilter || []).flat();

    const selectedAttendeesIds = attendeesFilterFlat
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          if (item?.firm_user_id) {
            return item.firm_user_id;
          }
          if (item?.user_group_id) {
            return item.user_group_id;
          }
        }
        return item;
      })
      .filter(Boolean);

    const flattenedUserOptions = (userList || []).flatMap((g) => g?.items || []);
    const firmUserListFlat = (firmUserList || []).flatMap((g) => g?.items || []);
    const allUsersAndGroups = [...flattenedUserOptions, ...firmUserListFlat];

    const firmUserIdsFromUsers = [];
    const groupIdsFromSelected = [];
    const processedIds = new Set();

    selectedAttendeesIds.forEach((selectedId) => {
      if (processedIds.has(selectedId)) return;

      const matchedItem = allUsersAndGroups.find((item) => {
        if (item?.firm_user_id) {
          const userId = item?.value || item?.client_cognito_username || item?.contact_id || item?.user_id || item?.firm_user_id;
          return userId === selectedId || item?.firm_user_id === selectedId;
        }
        if (item?.user_group_id && !item?.firm_user_id) {
          const groupId = item?.value || item?.user_group_id;
          return groupId === selectedId || item?.user_group_id === selectedId;
        }
        return false;
      });

      if (matchedItem) {
        if (matchedItem?.firm_user_id) {
          if (!processedIds.has(matchedItem.firm_user_id)) {
            const userId =
              matchedItem?.value ||
              matchedItem?.client_cognito_username ||
              matchedItem?.contact_id ||
              matchedItem?.user_id ||
              matchedItem?.firm_user_id;
            firmUserIdsFromUsers.push({
              firm_user_id: matchedItem.firm_user_id,
              list_google_event: highlightedGoogleIconIds.has(userId) || highlightedGoogleIconIds.has(matchedItem?.firm_user_id),
            });
            processedIds.add(matchedItem.firm_user_id);
          }
        } else if (matchedItem?.user_group_id && !matchedItem?.firm_user_id) {
          if (!groupIdsFromSelected.includes(matchedItem.user_group_id)) {
            groupIdsFromSelected.push(matchedItem.user_group_id);
            processedIds.add(matchedItem.user_group_id);
          }
        }
        processedIds.add(selectedId);
      }
    });

    const finalGroupIds = Array.from(new Set(groupIdsFromSelected));

    const firmUserFlatIds = selectedAttendeesIds;

    let obj = {
      event_type: calendarFilters.eventTypeFilter,
      // When showing firm-wide only, restrict to events category at API level
      category_filter: showFirmEvents ? 'event' : calendarFilters.categoryFilter,
      location: calendarFilters.locationFilter,
      conference_room: calendarFilters.conferenceRoomFilter,
      practice_area: calendarFilters.practiceAreaFilter,
      date_filter_param: date_filter_param,
      range_end_param: range_end_param,
      is_global_calendar: true,
      case_id: caseDetails?.case_id || calendarFilters.caseFilter?.id,
      next: '',
      user_timezone: getTimezoneOffset(userContext?.userDetails?.timezone),
      data: {
        firm_user: [...new Set(calendarFilters.attendeesFilter.flat())],
        firm_user_ids: firmUserIdsFromUsers,
        group_ids: finalGroupIds,
        get_calendar_items: calendarFilters?.attendeesFilter?.length === userList?.[0]?.items?.length + userList?.[1]?.items?.length,
        is_year_filter: currentView === 'multiMonthYear',
        // Firm-wide only switch from UI
        firm_wide_only: Boolean(showFirmEvents),
      },
    };
    listEvents(obj)
      .then((response) => {
        let events = response.data.events ? response.data.events : [];
        let cases = response.data.case ? response.data.case : [];
        let tasks = response.data.tasks ? response.data.tasks : [];
        let dob = response.data.dob ? response.data.dob : [];
        // Events (Meetings)
        if (events.length > 0) {
          events.forEach((item) => {
            const utcStart = item?.meeting_start_time;
            const utcEnd = item?.meeting_end_time;

            let start = momentTz.utc(utcStart).tz(userContext?.userDetails?.timezone);
            let end = momentTz.utc(utcEnd).tz(userContext?.userDetails?.timezone);
            const displayDate = momentTz.utc(currentStartDate).format('YYYY-MM-DD');

            if (item?.all_day) {
              item.start = start.format('YYYY-MM-DD');
              item.end = end.clone().add(1, 'day').format('YYYY-MM-DD');
            } else {
              item.start = start.format();
              item.end = end.format();
            }
            item.displayDate = displayDate;
            // normalize firm-wide flag from backend
            if (typeof item.firm_wide === 'undefined' && typeof item.is_firm_event !== 'undefined') {
              item.firm_wide = Boolean(item.is_firm_event);
            }
            // fallback: infer firm-wide from special accessible_list marker
            if (typeof item.firm_wide === 'undefined' || item.firm_wide === false) {
              if (Array.isArray(item?.accessible_list) && item.accessible_list.includes('__FIRM_WIDE__')) {
                item.firm_wide = true;
              }
            }
          });
        }
        // Cases (Statute of Limitations)
        if (cases.length > 0 && calendarSettings?.show_sol) {
          cases.forEach((item) => {
            const solDate = item?.important_dates?.statute_of_limitations;
            if (solDate) {
              let date = momentTz.utc(solDate).tz(userContext?.userDetails?.timezone);
              let end = date.clone().add(30, 'minutes'); // Ensure visible duration

              item.start = date.format();
              item.end = end.format();
              item.title = 'Statute Of Limitations';
              item.case_name = item.case_description?.case_name;
            }
          });
        }
        // Tasks
        if (tasks.length > 0) {
          tasks.forEach((item) => {
            const dueDate = item?.due_date;
            let date = momentTz.utc(dueDate).tz(userContext?.userDetails?.timezone);
            // Set fixed time 9:00 AM in that timezone
            let start = date.clone().hour(9).minute(0).second(0).millisecond(0);
            // Set fixed time 9:30 AM in that timezone
            let end = start.clone().add(30, 'minutes');

            item.start = start.format();
            item.end = end.format();
            item.title = item.task_name;
          });
        }
        if (dob.length > 0 && calendarSettings?.show_birthdays) {
          dob.forEach((item) => {
            let name = item.first_name || '';
            if (item.middle_name) name += ' ' + item.middle_name;
            if (item.last_name) name += ' ' + item.last_name;

            let originalDate = moment(item.dob);
            const currentYear = moment().year();
            originalDate.year(currentYear);

            let date = originalDate.tz(userContext?.userDetails?.timezone);
            let end = date.clone().add(30, 'minutes');

            item.start = date.format();
            item.end = end.format();
            item.title = `${name}'s birthday`;
          });
        }

        // Filter out firm-wide events for client calendars
        let data = [...events, ...cases, ...tasks, ...dob];
        if (isClientCalendar || userContext?.access_level === 'client') {
          data = data.filter((item) => !(item?.firm_wide === true));
        }

        // Apply conference room filter

        if (calendarFilters.conferenceRoomFilter) {
          data = data.filter((item) => {
            return item?.conference_room_id === calendarFilters.conferenceRoomFilter;
          });
        }

        let _eventList = data.filter((index) => index.start !== '');
        setEventList(_eventList);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getOutlookEvents() {
    if (user) {
      let { currentStartDate, currentEndDate } = currentDates;

      if (!currentStartDate && !currentEndDate) {
        return;
      }
      try {
        const ianaTimeZones = findIana(user?.timeZone);
        let events = await getUserWeekCalendar(authProvider, ianaTimeZones[0].valueOf(), currentStartDate, currentEndDate);
        setOutlookEventList(formatEventsOutLook(events));
      } catch (err) {
        displayError(err.message);
      }
    }
  }

  const getUserList = (selectedcase) => {
    setCalendarAttendeesLoaded(false);

    // To get user list for filter user avatars
    if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
      getCaseUserClientData(caseDetails?.case_id);
    } else if (selectedcase) {
      getCaseUserClientData(selectedcase);
      getFirmUsersListData();
    } else {
      getCaseUserClientData('');
      getFirmUsersListData();
    }
  };

  function getCaseUserClientData(case_id = '') {
    let obj = {
      caseId: case_id,
      all_contacts: case_id ? false : true,
      is_lead: isFromLeadSpecificModule ?? false,
      is_active: editEventModal ? false : true,
      active_groups: !editEventModal ? true : false,
    };
    getCalendarAttendees(obj)
      .then((response) => {
        let finalUserList = response?.data;

        if (editEventModal) {
          finalUserList = response?.data?.filter((user) => {
            const contactUserList =
              user.access_level === 'client' &&
              user?.status === 'ACTIVE' &&
              (user?.is_confirmed === true || user?.is_confirmed === undefined) &&
              user?.email;

            const activeContactUserList =
              user?.contact_id &&
              user?.status === 'ACTIVE' &&
              (user?.is_confirmed === true || user?.is_confirmed === undefined) &&
              user?.email;

            const eventContactUserList =
              user?.contact_id && eventDetails?.guests?.some((guest) => guest?.cognito_username === user?.client_cognito_username);

            const isActiveUser = user?.is_confirmed && user?.status === 'ACTIVE';
            const isArchivedConfirmed = user.res_type !== 'user-group' && (user.status === 'ARCHIVED' || user.is_confirmed === false);
            const isIndividualUser = eventDetails?.firm_users?.some(
              (firmUser) => firmUser?.cognito_username === user?.client_cognito_username
            );

            const isActiveGroup = user.res_type === 'user-group' && user.status === 'ACTIVE';
            const isArchiveGroupList =
              user.res_type === 'user-group' && user?.status === 'ARCHIVED' && eventDetails.group_list.includes(user.user_group_id);

            return (
              contactUserList ||
              activeContactUserList ||
              eventContactUserList ||
              isActiveUser ||
              (isArchivedConfirmed && isIndividualUser) ||
              isActiveGroup ||
              isArchiveGroupList
            );
          });
        }

        let combinedData = combineUserAndGroup(finalUserList);
        setUserList(combinedData);
        // for avatar list
        if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
          const responseData = response?.data;
          const filterDataWithoutGroups = responseData.filter((item) => item?.res_type !== 'user-group');
          // Sort users alphabetically
          const sortedUsers = filterDataWithoutGroups.sort((a, b) => {
            const nameA = a?.first_name || a?.title || '';
            const nameB = b?.first_name || b?.title || '';
            return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
          });
          setFirmUserList(ChangeCaseUserData(sortedUsers));
        }
        setCalendarAttendeesLoaded(true);
      })
      .catch((err) => {
        console.error(err);
        setCalendarAttendeesLoaded(true);
      });
  }

  function combineUserAndGroup(response) {
    const groupedContacts = {};

    if (response?.length > 0) {
      response?.forEach((element) => {
        const contactType = element?.res_type === 'user-group' ? 'Groups' : !element.contact_type ? 'Firm Users' : 'Contacts';
        function generateLabel(element) {
          if (element.contact_type === 'person' || element.first_name || element.middle_name || element.last_name) {
            return generateFullName(element);
          }
          return element.title;
        }

        if (!groupedContacts[contactType]) {
          groupedContacts[contactType] = {
            label: contactType,
            items: [],
          };
        }

        if (element?.res_type === 'user-group') {
          groupedContacts[contactType].items.push({
            label: element.group_name,
            value: element.user_group_id,
            ...element,
          });
        } else if (element?.access_level) {
          groupedContacts[contactType]?.items.push({
            label: generateLabel(element),
            value: element.client_cognito_username ? element.client_cognito_username : element.contact_id,
            access_level: element.access_level,
            ...element,
          });
        } else {
          const label = generateLabel(element);
          groupedContacts[contactType].items.push({
            label: label,
            value: element.client_cognito_username ? element.client_cognito_username : element.contact_id,
            email: element.email,
            is_create_client_portal: element.is_create_client_portal,
            access_level: element.access_level,
            ...element,
          });
        }
      });
    }
    const groupedContactsSorted = {
      ...groupedContacts,
      // Sort Firm Users by first_name
      ...(groupedContacts['Firm Users'] && {
        'Firm Users': {
          ...groupedContacts['Firm Users'],
          items: sortByKey(groupedContacts['Firm Users']?.items, 'first_name'),
        },
      }),
      // Sort Contacts by first_name
      ...(groupedContacts['Contacts'] && {
        Contacts: {
          ...groupedContacts['Contacts'],
          items: sortByKey(groupedContacts['Contacts']?.items, 'first_name'),
        },
      }),
      // Sort Groups by group_name (since groups use group_name instead of first_name)
      ...(groupedContacts['Groups'] && {
        Groups: {
          ...groupedContacts['Groups'],
          items: groupedContacts['Groups']?.items?.sort((a, b) =>
            (a.group_name?.toLowerCase() || '').localeCompare(b.group_name?.toLowerCase() || '')
          ),
        },
      }),
    };
    return Object.values(groupedContactsSorted);
  }

  function getFirmUsersListData() {
    totalCountEvent('user')
      .then((response) => {
        let obj = {
          status: 'ACTIVE',
          limit: response.data ?? 10,
          pagination_token: '',
          pagination_direction: 'forward',
          keyWord: '',
          next: '',
          is_include_contacts: true,
          is_detailed: false,
          is_confirmed_user: true,
          is_include_groups: true,
        };
        listUsers(obj)
          .then((response) => {
            const formattedList = formatMultiSelectContactList(response.data?.firm_users);
            const sortedList = formattedList.map((item) =>
              item?.code === 'Users' ? { ...item, items: sortByKey(item.items, 'first_name') } : item
            );
            setFirmUserList(sortedList);
          })
          .catch((err) => console.error(err));
      })
      .catch((error) => console.warn(error));
  }

  const ChangeCaseUserData = (data) =>
    data.map((v) => ({
      ...v,
      full_name: generateFullName(v),
      user_id: v.client_cognito_username || v.contact_id,
    }));

  function hideCreateEventModal() {
    setShowCreateEventModal(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('id');
    searchParams.delete('date');
    searchParams.delete('createEvent');
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    setSelectedEvent();
    setEventDetails();
    setEditEventModal();
    setUploadedAttachments();
    setShowUserFilter(false);
    setCalendateDayClickedDetails({});
  }

  function hidePreviewModal() {
    setShowEventPreviewModal(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('id');
    searchParams.delete('event');
    searchParams.delete('sk');
    searchParams.delete('date');
    searchParams.delete('createEvent');
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    setSelectedEvent();
    setEventDetails();
    setEditEventModal();
    setUploadedAttachments();
  }

  const generateFirmUserIdList = () => {
    const userIdList = firmUserList?.map((user, idx) => user?.user_id);
    return userIdList;
  };

  const handleShowReminder = (show) => {
    if (!show) {
      setShowReminder(false);
    } else {
      setShowReminder(true);
    }
  };
  const calenderDayClick = (arg = {}, clicked = false) => {
    setShowCreateEventModal(clicked);
    setCalendateDayClickedDetails(arg);
  };

  useEffect(() => {
    setShowCreateEventModal(Boolean(location?.state?.createEvent || queryParams.get('createEvent')));
  }, [location]);

  useEffect(() => {
    if (!Boolean(isClientCalendar)) {
      loadLocations();
      loadOfficeLocations();
    }
  }, []);

  const prevPracticeAreaRef = useRef();

  const { practiceAreaFilter } = calendarFilters;

  useEffect(() => {
    if (prevPracticeAreaRef.current !== practiceAreaFilter) {
      loadTypes(practiceAreaFilter);
      prevPracticeAreaRef.current = practiceAreaFilter;
    }
  }, [practiceAreaFilter]);

  useEffect(() => {
    if (showUserFilter && eventDetails?.case?.id) {
      getUserList(eventDetails?.case?.id);
    }
    if (showUserFilter && !eventDetails?.case?.id) {
      getUserList();
    }
    if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
      getUserList();
    }
  }, [showUserFilter, isFromCaseSpecificModule, isFromLeadSpecificModule]);

  useEffect(() => {
    if (isGlobal && !isClientCalendar && !isFromCaseSpecificModule && !isFromLeadSpecificModule) {
      getUserList();
    }
  }, [isGlobal, isClientCalendar, isFromCaseSpecificModule, isFromLeadSpecificModule]);

  useEffect(() => {
    if (userContext?.userDetails?.contact_id && isClientCalendar && notificationCounts) {
      Boolean(notificationCounts?.event_count) && readClientNotifications('event', userContext.userDetails.contact_id);
    }
  }, [userContext?.userDetails?.contact_id, notificationCounts, isClientCalendar]);

  useEffect(() => {
    if (!isClientCalendar) {
      getCalendarSettings()
        .then((res) => {
          let data = res?.data?.calendar_settings;

          setCalendarSettings(data);

          if (!data?.sync_with_outlook) {
            setUser();
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  // fetch calendar events
  useEffect(() => {
    if (currentDates) {
      getEventList();
    }
  }, [currentDates, calendarFilters, calendarSettings, showFirmEvents, calendarAttendeesLoaded, caseDetails]);

  // fetch outlook events
  useEffect(() => {
    if (calendarSettings) {
      fetchOutlookEvents();
    }
  }, [calendarSettings, user, currentDates]);

  useEffect(() => {
    if (firmUserList?.length > 0 && (isFromCaseSpecificModule || isFromLeadSpecificModule)) {
      setCalendarFilters((prevValues) => {
        return {
          ...prevValues,
          attendeesFilter: [...generateFirmUserIdList()],
        };
      });
    }
  }, [firmUserList]);

  useEffect(() => {
    if (!userContext || !isGlobal) return;

    const savedUsers = userContext?.firmDetails?.user_preferences?.filters?.calender?.users;

    const isStructuredFormat =
      Array.isArray(savedUsers) &&
      savedUsers.length > 0 &&
      typeof savedUsers[0] === 'object' &&
      (savedUsers[0]?.firm_user_id || savedUsers[0]?.user_group_id);

    if (isStructuredFormat) {
      const userIdsToSelect = [];
      const googleIconUserIds = new Set();

      const flattenedUserOptions = (userList || []).flatMap((g) => g?.items || []);
      const hasUserList = userList && userList.length > 0;
      const hasFirmUserList = firmUserList && firmUserList.length > 0;

      if (hasUserList || hasFirmUserList || (savedUsers && Array.isArray(savedUsers) && savedUsers.length > 0)) {
        let allUsersList = [];
        let combinedUserList = [];

        if (hasFirmUserList) {
          const isGroupedStructure = firmUserList[0]?.items && Array.isArray(firmUserList[0].items);

          if (isGroupedStructure) {
            // Global calendar - grouped structure
            allUsersList = firmUserList.reduce((acc, group) => {
              if (group?.items && Array.isArray(group.items)) {
                return [...acc, ...group.items];
              }
              return acc;
            }, []);
          } else {
            allUsersList = firmUserList;
          }

          combinedUserList = isGroupedStructure ? [...allUsersList, ...flattenedUserOptions] : allUsersList;
        } else {
          combinedUserList = flattenedUserOptions;
        }

        savedUsers.forEach((userObj) => {
          if (userObj?.user_group_id) {
            const firmUserListFlat = (firmUserList || []).flatMap((g) => g?.items || []);
            const allGroupsList = [...flattenedUserOptions, ...firmUserListFlat];

            const matchedGroup = allGroupsList.find(
              (u) => (u?.res_type === 'user-group' || (u?.user_group_id && !u?.firm_user_id)) && u?.user_group_id === userObj.user_group_id
            );

            if (matchedGroup) {
              const groupId = matchedGroup?.value || matchedGroup?.user_group_id;
              if (groupId && !userIdsToSelect.includes(groupId)) {
                userIdsToSelect.push(groupId);
              }
            } else {
              const archivedGroup = [...flattenedUserOptions, ...firmUserListFlat].find((u) => u?.user_group_id === userObj.user_group_id);

              if (archivedGroup) {
                const groupId = archivedGroup?.value || archivedGroup?.user_group_id || userObj.user_group_id;
                if (groupId && !userIdsToSelect.includes(groupId)) {
                  userIdsToSelect.push(groupId);
                }
              } else {
                if (userObj.user_group_id && !userIdsToSelect.includes(userObj.user_group_id)) {
                  userIdsToSelect.push(userObj.user_group_id);
                }
              }
            }
          } else if (userObj?.firm_user_id) {
            // Find user by firm_user_id
            const matchedUser = combinedUserList.find((u) => u?.firm_user_id === userObj.firm_user_id);

            if (matchedUser) {
              // Get user ID based on module type
              let userId;
              if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
                userId = matchedUser?.user_id || matchedUser?.client_cognito_username || matchedUser?.contact_id;
              } else {
                userId =
                  matchedUser?.value ||
                  matchedUser?.assignee_id ||
                  matchedUser?.user_id ||
                  matchedUser?.client_cognito_username ||
                  matchedUser?.contact_id ||
                  matchedUser?.firm_user_id;
              }

              if (userId) {
                userIdsToSelect.push(userId);

                // If list_google_event is true, add to highlighted Google icons
                if (userObj.list_google_event === true) {
                  googleIconUserIds.add(userId);
                }
              }
            }
          }
        });
      } else {
        savedUsers.forEach((userObj) => {
          if (userObj?.user_group_id) {
            if (!userIdsToSelect.includes(userObj.user_group_id)) {
              userIdsToSelect.push(userObj.user_group_id);
            }
          } else if (userObj?.firm_user_id && !userIdsToSelect.includes(userObj.firm_user_id)) {
          }
        });
      }

      if (userIdsToSelect.length === 0 && savedUsers && Array.isArray(savedUsers) && savedUsers.length > 0) {
        savedUsers.forEach((userObj) => {
          if (userObj?.user_group_id && !userIdsToSelect.includes(userObj.user_group_id)) {
            userIdsToSelect.push(userObj.user_group_id);
          }
        });
      }

      if (userIdsToSelect.length > 0) {
        setCalendarFilters((prev) => {
          const newFilter = [...userIdsToSelect];
          const prevFilter = prev.attendeesFilter || [];

          const prevFilterIds = prevFilter
            .map((item) => {
              if (typeof item === 'object' && item !== null) {
                return item?.user_group_id || item?.firm_user_id || item?.value || item?.id;
              }
              return item;
            })
            .filter(Boolean);

          const prevFilterIdsSet = new Set(prevFilterIds);
          const newFilterSet = new Set(newFilter);
          const areEqual =
            prevFilterIds.length === newFilter.length &&
            newFilter.every((id) => prevFilterIdsSet.has(id)) &&
            prevFilterIds.every((id) => newFilterSet.has(id));

          if (!areEqual) {
            return {
              ...prev,
              attendeesFilter: newFilter,
            };
          }

          if (prevFilter.length > 0 && typeof prevFilter[0] === 'object') {
            return {
              ...prev,
              attendeesFilter: newFilter,
            };
          }

          return prev;
        });
      }

      setHighlightedGoogleIconIds(googleIconUserIds);
    } else {
      const legacyUsers = Array.isArray(savedUsers) ? savedUsers : [];

      const savedGoogleIconIds = userContext?.firmDetails?.user_preferences?.filters?.calender?.google_icon_ids;

      setCalendarFilters((prev) => {
        const newFilter =
          Array.isArray(savedUsers) && savedUsers.length === 0
            ? []
            : legacyUsers.length > 0
            ? legacyUsers
            : savedUsers === undefined || savedUsers === null
            ? [getUserId()]
            : [];
        const currentFilter = prev.attendeesFilter || [];

        const areEqual = currentFilter.length === newFilter.length && currentFilter.every((id, idx) => id === newFilter[idx]);
        if (areEqual) return prev;

        return {
          ...prev,
          attendeesFilter: newFilter,
        };
      });

      if (Array.isArray(savedGoogleIconIds) && savedGoogleIconIds.length > 0) {
        setHighlightedGoogleIconIds(new Set(savedGoogleIconIds));
      } else {
        setHighlightedGoogleIconIds(new Set());
      }
    }
  }, [userContext, isGlobal, firmUserList, userList, isFromCaseSpecificModule, isFromLeadSpecificModule]);

  useEffect(() => {
    if (!userContext) return; // Wait until it's loaded

    // Only update filters that aren't dependent on structured users parsing
    const savedUsers = userContext?.firmDetails?.user_preferences?.filters?.calender?.users;
    const isStructuredFormat =
      Array.isArray(savedUsers) && savedUsers.length > 0 && typeof savedUsers[0] === 'object' && savedUsers[0]?.firm_user_id;

    setCalendarFilters((prev) => ({
      ...prev,
      locationFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_location || '' : '',
      eventTypeFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.event_type || '' : '',
      categoryFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_category || '' : '',
      attendeesFilter:
        isGlobal && !isStructuredFormat
          ? (() => {
              // If userContext has an empty array saved, respect it (user explicitly cleared)
              // Otherwise, if savedUsers is undefined/null, default to current user
              // Only restore from savedUsers if it exists and is not empty
              const newFilter =
                Array.isArray(savedUsers) && savedUsers.length === 0
                  ? [] // User explicitly cleared - respect empty array
                  : Array.isArray(savedUsers) && savedUsers.length > 0
                  ? savedUsers
                  : [getUserId()]; // Default to current user if no saved value
              const currentFilter = prev.attendeesFilter || [];
              // Compare arrays to avoid unnecessary updates
              const areEqual = currentFilter.length === newFilter.length && currentFilter.every((id, idx) => id === newFilter[idx]);
              if (areEqual) return prev.attendeesFilter;
              return newFilter;
            })()
          : prev.attendeesFilter,
      caseFilter:
        isGlobal && !isClientCalendar
          ? userContext?.firmDetails?.user_preferences?.filters?.calender?.case || ''
          : isClientCalendar
          ? convertCaseToFilterFormat(userContext?.clientSelectedCase)
          : '',
      conferenceRoomFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.conference_room || '' : '',
      practiceAreaFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_practice_area || '' : '',
    }));
  }, [isGlobal, isClientCalendar]);

  // Wrapper for setCalendarFilters that tracks manual updates
  const setCalendarFiltersWithTracking = useCallback((updater) => {
    setCalendarFilters((prev) => {
      const result = typeof updater === 'function' ? updater(prev) : updater;
      // Track the attendeesFilter value when manually set
      if (result?.attendeesFilter !== undefined) {
        const sortedFilter = [...result.attendeesFilter].sort();
        lastManualAttendeesFilterRef.current = JSON.stringify(sortedFilter);
        lastManualUpdateTimeRef.current = Date.now();
      }
      return result;
    });
  }, []);

  const contextPayload = useMemo(
    () => ({
      userContext,
      caseDetails,
      isFromCaseSpecificModule,
      isFromLeadSpecificModule,
      isClientCalendar,
      isGlobal,
      loadLocations,
      locationsList,
      officeLocationsList,
      typeList,
      // events
      eventList,
      setEventList,
      outlookEventList,
      setOutlookEventList,
      //modals
      showCreateEventModal,
      setShowCreateEventModal,
      loadTypes,
      showEventPreviewModal,
      setShowEventPreviewModal,
      selectedEvent,
      setSelectedEvent,
      editEventModal,
      setEditEventModal,
      editAllRecurringEvent,
      setEditAllRecurringEvent,
      getEventList,
      hideCreateEventModal,
      hidePreviewModal,
      uploadedAttachments,
      setUploadedAttachments,
      tempDeletedAttachments,
      setTempDeletedAttachments,
      currentView,
      setCurrentView,
      currentDates,
      setCurrentDates,
      //filters
      userList,
      setUserList,
      firmUserList,
      showCalendarSettings,
      setShowCalendarSettings,
      calendarSettings,
      setCalendarSettings,
      calendarFilters,
      setCalendarFilters,
      setCalendarFiltersWithTracking,
      showFirmEvents,
      setShowFirmEvents,
      eventDetails,
      setEventDetails,
      setLocationsList,
      isClosed,
      generateFirmUserIdList,
      showUserFilter,
      setShowUserFilter,
      getUserList,
      showReminder,
      handleShowReminder,
      isLocationLoading,
      isEventTypeLoading,
      calenderDayClick,
      calendarDayClickedDetails,
      setCalendateDayClickedDetails,
      highlightedGoogleIconIds,
      setHighlightedGoogleIconIds,
    }),
    [
      setCalendarFiltersWithTracking,
      userContext,
      locationsList,
      typeList,
      eventList,
      outlookEventList,
      showCreateEventModal,
      showEventPreviewModal,
      selectedEvent,
      editEventModal,
      editAllRecurringEvent,
      uploadedAttachments,
      tempDeletedAttachments,
      currentView,
      setCurrentView,
      currentDates,
      setCurrentDates,
      userList,
      firmUserList,
      showCalendarSettings,
      calendarFilters,
      calendarSettings,
      eventDetails,
      isClosed,
      showUserFilter,
      setShowUserFilter,
      getUserList,
      showReminder,
      handleShowReminder,
      isLocationLoading,
      isEventTypeLoading,
      calenderDayClick,
      calendarDayClickedDetails,
      setCalendateDayClickedDetails,
      highlightedGoogleIconIds,
    ]
  );

  return <CalendarContext.Provider value={contextPayload} {...props} />;
};
